import { TextField, Autocomplete, Box, IconButton, SxProps } from '@mui/material';
import { DragIndicator as DragIndicatorIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Controller, useForm } from 'react-hook-form';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { QueryExpressionForm } from './QueryExpressionForm';
import { CustomTheme } from '../../contexts/theme.context';
import { Conjunction, Operator, QueryExpression } from '../../../../types/constants/query-expression';
import { AttributeApiModel } from '../../../../types/models/attribute.model';
import { AttributeType } from '../../../../types/constants/attribute-type';

const operators = Object.values(Operator);

const conjunctions = Object.values(Conjunction);

type Props = {
  queryExpression: QueryExpression;
  properties: Pick<AttributeApiModel, 'name' | 'type' | 'values'>[];
  onChange: (field: keyof QueryExpression, value: string | null) => void;
  onDelete: () => void;
  dragHandleProps?: {
    ref: (element: HTMLElement | null) => void;
  } & React.DOMAttributes<HTMLElement>;
  isConjunction?: boolean;
  sx?: SxProps<CustomTheme>;
};

export const QueryExpressionItem = ({
  queryExpression,
  dragHandleProps,
  isConjunction,
  properties,
  onDelete,
  onChange,
  sx,
}: Props) => {
  const {
    control,
    register,
    setValue,
    formState: { errors },
    watch,
  } = useForm<QueryExpressionForm>({
    resolver: classValidatorResolver(QueryExpressionForm),
    values: queryExpression,
  });

  const propertyValue = watch('property');

  const property = properties.find(({ name }) => name === propertyValue);

  return (
    <Box sx={{ border: `1px solid #ccc`, ...sx }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid #ccc', py: 0.75 }}>
            {dragHandleProps && (
              <Box>
                <IconButton
                  {...dragHandleProps}
                  sx={{ marginTop: 0, marginBottom: 0, cursor: 'grab', '&:active': { cursor: 'grabbing' } }}
                >
                  <DragIndicatorIcon />
                </IconButton>
              </Box>
            )}
            <Controller
              control={control}
              name="property"
              render={({ field }) => (
                <Autocomplete
                  {...register('property')}
                  onChange={(_event, selectedOption) => {
                    field.onChange(selectedOption);
                    onChange('property', selectedOption);
                    setValue('value', '');
                    onChange('value', null);
                  }}
                  onBlur={field.onBlur}
                  value={field.value}
                  options={properties?.map(({ name }) => name) ?? []}
                  sx={{
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                      py: 0.126,
                      px: 1.126,
                    },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select a property"
                      error={Boolean(errors.property)}
                      helperText={errors.property?.message}
                    />
                  )}
                />
              )}
            />
            <Controller
              control={control}
              name="operator"
              render={({ field }) => (
                <Autocomplete
                  {...register('operator')}
                  onChange={(_event, selectedOption) => {
                    field.onChange(selectedOption);
                    onChange('operator', selectedOption);
                  }}
                  onBlur={field.onBlur}
                  value={field.value}
                  getOptionLabel={(operator) => operator ?? ''}
                  options={operators}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      py: 0.126,
                      px: 1.126,
                    },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select an operator"
                      sx={{ marginTop: 0, marginBottom: 0 }}
                      error={Boolean(errors.operator)}
                      helperText={errors.operator?.message}
                    />
                  )}
                />
              )}
            />
            {property?.type === AttributeType.LIST ? (
              <Controller
                control={control}
                name="value"
                render={({ field }) => (
                  <Autocomplete
                    {...register('value')}
                    onChange={(_event, selectedOption) => {
                      field.onChange(selectedOption);
                      onChange('value', selectedOption);
                    }}
                    onBlur={field.onBlur}
                    value={field.value}
                    sx={{
                      width: '10rem',
                      '& .MuiOutlinedInput-root': {
                        py: 0.126,
                        px: 1.126,
                      },
                      flex: 1,
                    }}
                    getOptionLabel={(value) => value ?? ''}
                    options={property?.values ?? []}
                    renderInput={(params) => (
                      <TextField
                        placeholder="Select a value"
                        {...params}
                        error={Boolean(errors.value)}
                        helperText={errors.value?.message}
                      />
                    )}
                  />
                )}
              />
            ) : (
              <TextField
                {...register('value')}
                variant="outlined"
                error={Boolean(errors.value)}
                helperText={errors.value?.message}
                placeholder="Enter a value"
                sx={{ marginTop: 0, marginBottom: 0, flex: 1 }}
                slotProps={{ htmlInput: { maxLength: 150 } }}
                margin="dense"
                size="small"
                type={
                  property?.type === AttributeType.INTEGER || property?.type === AttributeType.DOUBLE
                    ? 'number'
                    : 'text'
                }
                onChange={(e) => onChange('value', e.target.value)}
              />
            )}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton aria-label="Delete expression" onClick={onDelete}>
                <DeleteIcon />
              </IconButton>
            </Box>
          </Box>
          {isConjunction && (
            <Box sx={{ py: 0.75, display: 'flex', justifyContent: 'center', flex: 1 }}>
              <Controller
                control={control}
                name="conjunction"
                render={({ field }) => (
                  <Autocomplete
                    {...register('conjunction')}
                    onChange={(_event, selectedOption) => {
                      field.onChange(selectedOption);
                      onChange('conjunction', selectedOption);
                    }}
                    onBlur={field.onBlur}
                    value={field.value}
                    sx={{
                      width: '10rem',
                      '& .MuiOutlinedInput-root': {
                        py: 0.126,
                        px: 1.126,
                      },
                    }}
                    getOptionLabel={(conjunction) => conjunction ?? ''}
                    options={conjunctions}
                    renderInput={(params) => (
                      <TextField
                        placeholder="Select a conjunction"
                        {...params}
                        error={Boolean(errors.conjunction)}
                        helperText={errors.conjunction?.message}
                      />
                    )}
                  />
                )}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
